import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  CheckCircle,
  Loader2,
  TrendingUp,
  Gift,
  Settings,
  Building,
  AlertCircle,
  Info
} from 'lucide-react';
import { toast } from 'sonner';

const CreatorSettings = () => {
  const { user, token, API } = useContext(AuthContext);
  const [searchParams] = useSearchParams();

  // Payment providers status
  const [razorpayStatus, setRazorpayStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [connectingProvider, setConnectingProvider] = useState(null);

  // Razorpay onboarding form
  const [razorpayForm, setRazorpayForm] = useState({
    pan_number: '',
    bank_account_number: '',
    bank_ifsc: '',
    account_holder_name: ''
  });

  // Validation state
  const [ifscError, setIfscError] = useState('');
  const [isValidatingIFSC, setIsValidatingIFSC] = useState(false);

  // Earnings
  const [earnings, setEarnings] = useState(null);
  const [loadingEarnings, setLoadingEarnings] = useState(true);

  useEffect(() => {
    fetchAllStatus();
    fetchEarnings();
  }, []);

  const fetchAllStatus = async () => {
    try {
      // Fetch user data for Razorpay status
      const userRes = await axios.get(`${API}/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setRazorpayStatus({
        connected: userRes.data.razorpay_onboarded || false,
        account_id: userRes.data.razorpay_account_id,
        details: userRes.data.razorpay_bank_details || {}
      });

      // Pre-fill form if connected (optional, or keep empty for security)
      if (userRes.data.razorpay_onboarded && userRes.data.razorpay_bank_details) {
        setRazorpayForm({
          pan_number: '', // Don't autofill sensitive
          bank_account_number: userRes.data.razorpay_bank_details.account_number || '',
          bank_ifsc: userRes.data.razorpay_bank_details.ifsc || '',
          account_holder_name: userRes.data.razorpay_bank_details.holder_name || ''
        });
      }

    } catch (error) {
      console.error('Error fetching status:', error);
    } finally {
      setLoadingStatus(false);
    }
  };

  const fetchEarnings = async () => {
    try {
      const response = await axios.get(`${API}/creator/earnings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEarnings(response.data);
    } catch (error) {
      console.error('Error fetching earnings:', error);
    } finally {
      setLoadingEarnings(false);
    }
  };

  const validateIFSC = (code) => {
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    return ifscRegex.test(code);
  };

  const handleIFSCChange = async (e) => {
    const code = e.target.value.toUpperCase();
    setRazorpayForm(prev => ({ ...prev, bank_ifsc: code }));

    if (code.length === 11) {
      setIsValidatingIFSC(true);
      if (validateIFSC(code)) {
        setIfscError('');
        // Optional: You could fetch bank name here if you had an API
        // For now, we just validate the format
      } else {
        setIfscError('Invalid IFSC Code format');
      }
      setIsValidatingIFSC(false);
    } else if (code.length > 0 && code.length !== 11) {
      setIfscError('IFSC Code must be 11 characters');
    } else {
      setIfscError('');
    }
  };

  const handleConnectRazorpay = async () => {
    if (!razorpayForm.account_holder_name.trim()) {
      toast.error('Please enter Account Holder Name');
      return;
    }
    if (!razorpayForm.bank_account_number.trim()) {
      toast.error('Please enter Bank Account Number');
      return;
    }
    if (!validateIFSC(razorpayForm.bank_ifsc)) {
      toast.error('Please enter a valid IFSC Code');
      return;
    }

    setConnectingProvider('razorpay');
    try {
      const response = await axios.post(
        `${API}/payments/onboard`,
        {
          provider: 'razorpay',
          ...razorpayForm
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Bank details saved successfully!');
      setRazorpayStatus({
        connected: true,
        details: {
          holder_name: razorpayForm.account_holder_name,
          account_number: razorpayForm.bank_account_number.slice(-4), // Mock masking
          ifsc: razorpayForm.bank_ifsc
        }
      });
      fetchAllStatus();
    } catch (error) {
      console.error('Error connecting Razorpay:', error);
      toast.error(error.response?.data?.detail || 'Failed to save bank details');
    } finally {
      setConnectingProvider(null);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen hero-background flex items-center justify-center">
        <Card className="bg-gray-800/90 border-purple-500/30 p-8">
          <p className="text-white">Please log in to access creator settings.</p>
          <Link to="/login" className="text-purple-400 hover:text-purple-300 mt-4 block">
            Go to Login →
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-background py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-900/20">
            <Settings className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white">
            Creator Settings
          </h1>
          <p className="text-gray-400 mt-2">Manage your bank details and view earnings</p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Earnings Section (Moved to top/left for visibility) */}
          <Card className="bg-gray-800/80 backdrop-blur border-purple-500/30 md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-white">
                <TrendingUp className="w-5 h-5 text-green-400" />
                <span>Your Earnings</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingEarnings ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gray-700/50 rounded-xl p-5 text-center border border-gray-600/30">
                      <p className="text-gray-400 text-sm mb-1">Total Earnings</p>
                      <p className="text-3xl font-bold text-green-400">
                        ₹{earnings?.earnings?.total_earnings?.toFixed(2) || '0.00'}
                      </p>
                    </div>

                    <div className="bg-gray-700/50 rounded-xl p-5 text-center border border-gray-600/30">
                      <p className="text-gray-400 text-sm mb-1">Tips Received</p>
                      <p className="text-3xl font-bold text-yellow-400">
                        {earnings?.earnings?.tips_count || 0}
                      </p>
                    </div>

                    <div className="bg-gray-700/50 rounded-xl p-5 text-center border border-gray-600/30">
                      <p className="text-gray-400 text-sm mb-1">Platform Fee</p>
                      <p className="text-3xl font-bold text-orange-400">
                        {earnings?.platform_fee_percent || 10}%
                      </p>
                    </div>
                  </div>

                  {/* Recent Tips */}
                  <div className="mt-6">
                    <h3 className="text-white font-semibold mb-4 flex items-center">
                      <Gift className="w-4 h-4 mr-2 text-purple-400" />
                      Recent Tips
                    </h3>
                    {earnings?.recent_tips?.length > 0 ? (
                      <div className="space-y-3">
                        {earnings.recent_tips.map((tip, index) => (
                          <div key={index} className="flex items-center justify-between p-4 bg-gray-700/30 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors">
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="text-white font-medium">₹{tip.amount?.toFixed(2)}</p>
                                <span className="text-xs text-gray-500 bg-gray-800 px-2 py-0.5 rounded-full">Tip</span>
                              </div>
                              <p className="text-gray-500 text-xs mt-1">
                                {new Date(tip.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <span className="text-green-400 font-semibold bg-green-900/20 px-3 py-1 rounded-full text-sm">
                              +₹{tip.creator_amount?.toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-gray-700/20 rounded-xl border border-dashed border-gray-700">
                        <Gift className="w-10 h-10 mx-auto mb-3 text-gray-600" />
                        <p className="text-gray-400">No tips received yet.</p>
                        <p className="text-gray-600 text-sm mt-1">Share your comics to start earning!</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Bank Details Section */}
          <Card className="bg-gray-800/80 backdrop-blur border-blue-500/30 md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-white">
                <Building className="w-5 h-5 text-blue-400" />
                <span>Bank Account Details</span>
              </CardTitle>
              <CardDescription className="text-gray-400">
                To receive your earnings, please provide your Indian bank account details.
                <br />
                Payments are processed via secure bank transfer (NEFT/IMPS).
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingStatus ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Status Banner */}
                  <div className={`p-4 rounded-xl flex items-center justify-between ${razorpayStatus?.connected
                      ? 'bg-green-500/10 border border-green-500/30'
                      : 'bg-blue-500/10 border border-blue-500/30'
                    }`}>
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${razorpayStatus?.connected ? 'bg-green-500/20' : 'bg-blue-500/20'
                        }`}>
                        <Building className={`w-5 h-5 ${razorpayStatus?.connected ? 'text-green-400' : 'text-blue-400'
                          }`} />
                      </div>
                      <div>
                        <p className={`font-medium ${razorpayStatus?.connected ? 'text-green-400' : 'text-blue-400'
                          }`}>
                          {razorpayStatus?.connected ? 'Bank Account Connected' : 'No Bank Account Linked'}
                        </p>
                        {razorpayStatus?.connected && (
                          <p className="text-xs text-gray-400 mt-0.5">
                            Your earnings will be settled to this account.
                          </p>
                        )}
                      </div>
                    </div>
                    {razorpayStatus?.connected && (
                      <CheckCircle className="w-6 h-6 text-green-500" />
                    )}
                  </div>

                  {/* Form */}
                  <div className="grid gap-6 md:grid-cols-2">
                    <div className="md:col-span-2">
                      <Label className="text-gray-300">Account Holder Name</Label>
                      <Input
                        value={razorpayForm.account_holder_name}
                        onChange={(e) => setRazorpayForm({ ...razorpayForm, account_holder_name: e.target.value })}
                        placeholder="e.g. Rahul Sharma"
                        className="bg-gray-700/50 border-gray-600 text-white mt-1.5 focus:border-blue-500 transition-colors"
                      />
                    </div>

                    <div>
                      <Label className="text-gray-300">Bank Account Number</Label>
                      <Input
                        type="password"
                        value={razorpayForm.bank_account_number}
                        onChange={(e) => setRazorpayForm({ ...razorpayForm, bank_account_number: e.target.value })}
                        placeholder="Enter account number"
                        className="bg-gray-700/50 border-gray-600 text-white mt-1.5 focus:border-blue-500 transition-colors"
                      />
                    </div>

                    <div>
                      <Label className="text-gray-300">IFSC Code</Label>
                      <div className="relative">
                        <Input
                          value={razorpayForm.bank_ifsc}
                          onChange={handleIFSCChange}
                          placeholder="e.g. SBIN0001234"
                          className={`bg-gray-700/50 border-gray-600 text-white mt-1.5 focus:border-blue-500 transition-colors uppercase ${ifscError ? 'border-red-500 focus:border-red-500' : ''}`}
                          maxLength={11}
                        />
                        {isValidatingIFSC && (
                          <Loader2 className="absolute right-3 top-[18px] w-4 h-4 animate-spin text-gray-400" />
                        )}
                      </div>
                      {ifscError && (
                        <p className="text-red-400 text-xs mt-1 flex items-center">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          {ifscError}
                        </p>
                      )}
                      {!ifscError && razorpayForm.bank_ifsc.length === 11 && (
                        <p className="text-green-400 text-xs mt-1 flex items-center">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Valid Format
                        </p>
                      )}
                      <p className="text-gray-500 text-xs mt-1 flex items-center gap-1">
                        <Info className="w-3 h-3" /> 11 characters, usually found on cheque book
                      </p>
                    </div>

                    <div className="md:col-span-2">
                      <Label className="text-gray-300">PAN Number (Optional)</Label>
                      <Input
                        value={razorpayForm.pan_number}
                        onChange={(e) => setRazorpayForm({ ...razorpayForm, pan_number: e.target.value.toUpperCase() })}
                        placeholder="e.g. ABCDE1234F"
                        className="bg-gray-700/50 border-gray-600 text-white mt-1.5 focus:border-blue-500 transition-colors uppercase"
                      />
                    </div>
                  </div>

                  <Button
                    onClick={handleConnectRazorpay}
                    disabled={connectingProvider === 'razorpay' || !!ifscError}
                    className="w-full bg-blue-600 hover:bg-blue-500 text-white font-medium py-6 rounded-xl shadow-lg shadow-blue-900/20 transition-all hover:translate-y-[-1px]"
                  >
                    {connectingProvider === 'razorpay' ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Saving Details...</>
                    ) : (
                      razorpayStatus?.connected ? 'Update Bank Details' : 'Save Bank Details'
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CreatorSettings;
